// src/components/crypto/simpleE2EE.ts
import nacl from "tweetnacl";
import naclUtil from "tweetnacl-util";
import api from "../api/axios";

/* ADD: absolute /api base (works in dev and prod) */
const API_BASE =
  (api?.defaults?.baseURL && api.defaults.baseURL.replace(/\/+$/, "")) ||
  (typeof window !== "undefined" ? `${window.location.origin}/api` : "http://localhost:3001/api");

/* ADD: helper to build the message-ID download URL */
export const idDownload = (messageId: string, part: "media" | "poster" = "media") =>
  `${API_BASE}/message/${encodeURIComponent(messageId)}/download?part=${part}`;

/**
 * -------------------------------------------------
 * SIMPLE PER-USER END-TO-END ENCRYPTION
 * -------------------------------------------------
 * - One keypair per user (public/private)
 * - Public key published via API
 * - Private key stored in localStorage (or fetched after login, encrypted)
 * - Text messages: sealed-box style (ephemeral sender key embedded in payload)
 * - Media: client-side AES-GCM file encryption; AES key wrapped via sealed-box
 */

/** Keypair (base64-encoded for storage/transmission) */
export interface UserKeypair {
  publicKey: string;  // base64 (standard)
  secretKey: string;  // base64 (standard)
}

/** Encrypted text payload (base64 fields) */
export interface CipherPayload {
  nonce: string;       // base64 (std or urlsafe accepted on decode)
  ciphertext: string;  // base64 (std or urlsafe)
  ephPub: string;      // base64 (std or urlsafe) — ephemeral sender public key
}

/* ===================== Key Management ===================== */
// add this export near the top with the other helpers
export function publicKeyFromSecret(secretB64: string): string {
  let secU8 = naclUtil.decodeBase64(
    secretB64.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(secretB64.length/4)*4, "=")
  );
  if (secU8.length === 64) secU8 = secU8.slice(0, 32); // tolerate 64-byte stores
  if (secU8.length !== 32) throw new Error("Invalid box secret key length");
  const kp = nacl.box.keyPair.fromSecretKey(secU8);
  return naclUtil.encodeBase64(kp.publicKey);
}

export function generateKeypair(): UserKeypair {
  const kp = nacl.box.keyPair();
  return {
    publicKey: naclUtil.encodeBase64(kp.publicKey),
    secretKey: naclUtil.encodeBase64(kp.secretKey),
  };
}

export function saveMySecretKey(secretKey: string) {
  localStorage.setItem("my:secretKey", secretKey);
}

export function loadMySecretKey(): string | null {
  return localStorage.getItem("my:secretKey");
}

/**
 * Fetch recipient's public key (base64 or base64url) from your backend.
 * Your API returns { publicKey: string } via /"users" now chatkey/public/:id/chat-key
 */
export async function getPublicKeyForUser(userId: string): Promise<string | null> {
  try {
    const { data } = await api.get(`/chatkey/public/${userId}/chat-key`);
    const k = data?.publicKey || data?.key || data;
    return typeof k === "string" && k.length > 0 ? k : null;
  } catch (e) {
    console.warn("getPublicKeyForUser failed:", e);
    return null;
  }
}

/* ===================== Base64 helpers ===================== */

// Normalize both standard base64 and base64url to standard base64 with padding
function normalizeB64ToStd(input: string): string {
  const s = (input || "").trim().replace(/-/g, "+").replace(/_/g, "/");
  const pad = s.length % 4 ? "=".repeat(4 - (s.length % 4)) : "";
  return s + pad;
}

// Decode any b64 variant to Uint8Array
function fromAnyB64(input: string): Uint8Array {
  return naclUtil.decodeBase64(normalizeB64ToStd(input));
}

// Encode to base64url (no padding)
function toBase64url(bytes: Uint8Array): string {
  const b64 = naclUtil.encodeBase64(bytes);
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

// TS-safe helper: hand WebCrypto a *real* ArrayBuffer (not ArrayBufferLike)
const toExactArrayBuffer = (u8: Uint8Array): ArrayBuffer => {
  const ab = new ArrayBuffer(u8.byteLength);
  new Uint8Array(ab).set(u8);
  return ab;
};

/* ===================== Text Encryption ===================== */
/**
 * Encrypt plaintext for recipient using sealed-box style:
 * - Generate ephemeral keypair
 * - Derive shared key with recipientPub & ephSecret
 * - nacl.box (XSalsa20-Poly1305) with random nonce
 * - Include ephPub in the payload so the recipient can decrypt
 */
export function encryptMessage(
  plaintext: string,
  recipientPublicKeyB64: string,
  mySecretKeyB64?: string // unused here but kept for API symmetry/forward-compat
): CipherPayload {
  const recipientPub = naclUtil.decodeBase64(
    normalizeB64ToStd(recipientPublicKeyB64)
  );

  // ephemeral sender keys
  const eph = nacl.box.keyPair();
  const nonce = nacl.randomBytes(24);
  const msgUint8 = naclUtil.decodeUTF8(plaintext);

  const box = nacl.box(msgUint8, nonce, recipientPub, eph.secretKey);

  return {
    nonce: naclUtil.encodeBase64(nonce),
    ciphertext: naclUtil.encodeBase64(box),
    ephPub: naclUtil.encodeBase64(eph.publicKey),
  };
}

/**
 * Decrypt a sealed-box payload using only *your* secret key.
 * Accepts an object or a JSON string; accepts base64 or base64url fields.
 */
export function decryptMessage(payload: CipherPayload | string, mySecretKeyB64: string): string {
  const env: CipherPayload = typeof payload === "string" ? JSON.parse(payload) : payload;
  if (!env || !env.nonce || !env.ciphertext || !env.ephPub) throw new Error("Invalid payload");
  const nonce = fromAnyB64(env.nonce);
  const ct    = fromAnyB64(env.ciphertext);
  const eph   = fromAnyB64(env.ephPub);
  const sec   = fromAnyB64(mySecretKeyB64);
  if (nonce.length !== 24) throw new Error("Bad nonce");
  if (eph.length !== 32) throw new Error("Bad ephPub");
  if (sec.length !== 32 && sec.length !== 64) throw new Error("Bad secret key");
  const sec32 = sec.length === 64 ? sec.slice(0, 32) : sec;

  const out = nacl.box.open(ct, nonce, eph, sec32);
  if (!out) throw new Error("Decryption failed");
  return naclUtil.encodeUTF8(out);
}


/* ===================== Media Encryption ===================== */
/**
 * Encrypt a media Blob client-side with AES-GCM (256-bit).
 * Returns:
 *  - encrypted Blob
 *  - key (base64url) to wrap with encryptMessage and send as mediaKeyEnvelope
 *  - iv (base64url) to store alongside media
 */
export async function encryptMediaBlob(
  blob: Blob
): Promise<{
  encrypted: Blob;
  keyB64u: string;
  ivB64u: string;
  mime: string;
  scheme: "xsalsa20poly1305";
}> {
  // Read raw bytes
  const buf = await blob.arrayBuffer();
  const plain = new Uint8Array(buf);

  // XSalsa20-Poly1305 (NaCl secretbox)
  const keyU8 = nacl.randomBytes(32);  // 32-byte key
  const ivU8  = nacl.randomBytes(24);  // 24-byte nonce
  const ct    = nacl.secretbox(plain, ivU8, keyU8);

  return {
    encrypted: new Blob([toExactArrayBuffer(ct)], { type: "application/octet-stream" }),
    keyB64u: toBase64url(keyU8),
    ivB64u:  toBase64url(ivU8),
    mime: blob.type || "application/octet-stream",
    scheme: "xsalsa20poly1305",
  };
  
}



/**
 * Decrypt an encrypted media URL (download → decrypt → ObjectURL).
 * Use the same IV and the unwrapped key (both base64url).
 * Optionally pass an Authorization header for private origins.
 */
export async function decryptMediaFromUrlToObjectURL(
  mediaUrl: string,
  ivB64u: string,
  keyB64u: string,
  token?: string | null,
  mimeHint?: string,
  scheme?: "aes-gcm" | "xsalsa20poly1305"
): Promise<string> {
  const ivU8  = fromAnyB64(ivB64u);
  const keyU8 = fromAnyB64(keyB64u);

  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  const res = await fetch(mediaUrl, { headers });
  if (!res.ok) throw new Error(`Media fetch failed ${res.status}`);
  const encBuf = await res.arrayBuffer();

  // Decide which algorithm to use:
  // - explicit scheme wins
  // - otherwise: 24-byte iv → xsalsa/secretbox, else AES-GCM
  const algo: "aes-gcm" | "xsalsa20poly1305" =
    scheme ?? (ivU8.length === 24 ? "xsalsa20poly1305" : "aes-gcm");

  let decBuf: ArrayBuffer;

  if (algo === "aes-gcm") {
    if (ivU8.length !== 12) {
      throw new Error(`Bad AES-GCM IV length: ${ivU8.length}`);
    }

    const key = await crypto.subtle.importKey(
      "raw",
      toExactArrayBuffer(keyU8),
      { name: "AES-GCM" },
      false,
      ["decrypt"]
    );

    decBuf = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: toExactArrayBuffer(ivU8) },
      key,
      encBuf
    );
  } else {
    // XSalsa20-Poly1305 (NaCl secretbox) — matches mobile fallback
    if (ivU8.length !== 24) throw new Error(`Bad XSalsa nonce length: ${ivU8.length}`);
    if (keyU8.length !== 32) throw new Error(`Bad XSalsa key length: ${keyU8.length}`);

    const ct = new Uint8Array(encBuf);
    const pt = nacl.secretbox.open(ct, ivU8, keyU8);
    if (!pt) throw new Error("XSalsa secretbox open failed");
    decBuf = toExactArrayBuffer(pt);
  }

  return URL.createObjectURL(
    new Blob([decBuf], { type: mimeHint || "application/octet-stream" })
  );
}

export async function publishMyChatPublicKey(secretKeyB64: string) {
  const pub = publicKeyFromSecret(secretKeyB64);
  await api.post(`/chatkey/me/chat-pub`, {
    publicKey: pub,
    secretKey: secretKeyB64,   // 👈 NEW
  });
}

export async function getMySecretFromServer(): Promise<string | null> {
  try {
    const { data } = await api.get('/chatkey/me/chat-secret');
    const k = data?.secretKey || data?.secret || null;
    return typeof k === 'string' && k.length > 0 ? k : null;
  } catch (e) {
    console.warn('getMySecretFromServer failed:', e);
    return null;
  }
}



/* ===================== Media Key Wrapping ===================== */
/**
 * Wrap (encrypt) the AES-GCM media key for the recipient, using the same
 * sealed-box message format.
 */
export function wrapMediaKeyForRecipient(
  keyB64u: string,
  recipientPublicKeyB64: string,
  mySecretKeyB64?: string
): CipherPayload {
  return encryptMessage(keyB64u, recipientPublicKeyB64, mySecretKeyB64);
}

/**
 * Unwrap (decrypt) the AES-GCM media key you received in a message.
 */
export function unwrapMediaKey(
  mediaKeyEnvelope: CipherPayload | string,
  mySecretKeyB64: string
): string {
  return decryptMessage(mediaKeyEnvelope, mySecretKeyB64);
}

/** Ensure this browser has a usable NaCl secret key.
 *  Order:
 *    1) localStorage → use it
 *    2) /chatkey/me/chat-secret → save + use
 *    3) generate new keypair → publish {pub, secret} → save + use
 */
export async function ensureMySecretKeyAvailable(): Promise<string> {
  const local = loadMySecretKey();
  if (local && local.trim().length > 0) return local;

  const fromServer = await getMySecretFromServer();
  if (fromServer && fromServer.trim().length > 0) {
    saveMySecretKey(fromServer);
    return fromServer;
  }

  throw new Error('No server-side chat key available');
}